/** Owner inquiry / feedback — local queue for Coco, detachable product path. */

export type OwnerInquiryTopic =
  | "pricing"
  | "reservation"
  | "schedule"
  | "integration"
  | "ui"
  | "field"
  | "other";

export const OWNER_INQUIRY_TOPICS: {
  id: OwnerInquiryTopic;
  labelJa: string;
}[] = [
  { id: "field", labelJa: "現場メモ（出先の気づき）" },
  { id: "pricing", labelJa: "カット料金・毛玉・オゾンバス" },
  { id: "reservation", labelJa: "予約・新規登録" },
  { id: "schedule", labelJa: "スケジュール／グルーミング中表示" },
  { id: "integration", labelJa: "既存デジタルシステム連携" },
  { id: "ui", labelJa: "画面・操作（見づらい／ダサい含む）" },
  { id: "other", labelJa: "その他" },
];

/** Max images per inquiry (device storage + share size). */
export const OWNER_INQUIRY_MAX_IMAGES = 4;

export type OwnerInquiryImage = {
  id: string;
  name: string;
  /** compressed jpeg/png data URL */
  dataUrl: string;
};

export type OwnerInquiryStatus = "queued" | "done";

export type OwnerInquiry = {
  id: string;
  topic: OwnerInquiryTopic;
  body: string;
  contact: string;
  images: OwnerInquiryImage[];
  createdAt: number;
  /** queued = waiting for Coco; done = revised / closed */
  status: OwnerInquiryStatus;
  /** who picks up the queue (product default: ココ) */
  assignee: string;
};

export type OwnerInquiryInput = {
  topic: OwnerInquiryTopic;
  body: string;
  contact: string;
  images?: OwnerInquiryImage[];
};

export function createInquiryId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `inq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function validateOwnerInquiry(input: OwnerInquiryInput): string | null {
  const images = input.images ?? [];
  if (images.length > OWNER_INQUIRY_MAX_IMAGES) {
    return `画像は最大${OWNER_INQUIRY_MAX_IMAGES}枚までです。`;
  }
  if (!input.body.trim() && images.length === 0) {
    return "内容を書くか、画像を貼ってください。";
  }
  if (input.body.trim() && input.body.trim().length < 5 && images.length === 0) {
    return "もう少し具体的に書いてください。";
  }
  if (input.body.trim() && input.body.trim().length < 2 && images.length > 0) {
    return "短い一言でもよいので内容を書いてください。";
  }
  return null;
}

export function topicLabelJa(topic: OwnerInquiryTopic): string {
  return OWNER_INQUIRY_TOPICS.find((t) => t.id === topic)?.labelJa ?? topic;
}

export function normalizeOwnerInquiry(raw: Partial<OwnerInquiry> & {
  id: string;
  topic: OwnerInquiryTopic;
  body: string;
  createdAt: number;
}): OwnerInquiry {
  const legacyStatus = (raw as { status?: string }).status;
  const status: OwnerInquiryStatus =
    legacyStatus === "done" ? "done" : "queued";
  return {
    id: raw.id,
    topic: raw.topic,
    body: raw.body ?? "",
    contact: raw.contact ?? "",
    images: Array.isArray(raw.images) ? raw.images : [],
    createdAt: raw.createdAt,
    status,
    assignee: raw.assignee?.trim() || "ココ",
  };
}

export function buildInquiryShareText(input: {
  topic: OwnerInquiryTopic;
  body: string;
  contact: string;
  imageCount: number;
  assignee?: string;
}): string {
  const who = input.assignee?.trim() || "ココ";
  const lines = [
    `【DSS → ${who}】${topicLabelJa(input.topic)}`,
    "",
    input.body.trim() || "（本文なし・画像のみ）",
    "",
    `連絡先: ${input.contact.trim() || "（未記入）"}`,
    `画像: ${input.imageCount}枚`,
    "",
    "改修キューに入れてください。",
  ];
  return lines.join("\n");
}

export function buildInquiryMailto(input: {
  to: string;
  topic: OwnerInquiryTopic;
  body: string;
  contact: string;
  imageCount?: number;
  assignee?: string;
}): string {
  const who = input.assignee?.trim() || "ココ";
  const subject = encodeURIComponent(
    `[DSS → ${who}] ${topicLabelJa(input.topic)}`,
  );
  const body = encodeURIComponent(
    buildInquiryShareText({
      topic: input.topic,
      body: input.body,
      contact: input.contact,
      imageCount: input.imageCount ?? 0,
      assignee: who,
    }),
  );
  return `mailto:${input.to}?subject=${subject}&body=${body}`;
}
